// =====================================================================
// E-invoicing mandate data — one entry per /tax/{country} page.
// Adding a new country = add an entry here. The TaxPage template renders it.
// =====================================================================

export const TAX_PAGES = {
  ksa: {
    slug: "ksa",
    countryCode: "SA",
    hero: {
      eyebrow: "ZATCA | Saudi Arabia | Fatoora Phase II",
      h1: "ZATCA e-invoicing for your ERP",
      subhead:
        "Phase 2 (Integration) compliance for Saudi Arabia — UBL 2.1 XML, cryptographic stamp, QR and clearance — integrated with SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally or Sage. ASP-neutral.",
    },
    snapshot: [
      { label: "Authority", value: "ZATCA (Fatoora)" },
      { label: "Format", value: "UBL 2.1 + KSA extensions" },
      { label: "Phase", value: "Phase 2, wave-based" },
      { label: "Cloudare role", value: "Implementation, ASP-neutral" },
    ],
    whyTitle: "Why Cloudare for ZATCA",
    whyLede:
      "Phase 2 is an integration project, not a form change. We connect your ERP to ZATCA cleanly so invoices clear on the first attempt.",
    features: [
      {
        icon: "CalendarCheck",
        title: "All Waves",
        body: "Wave-aligned readiness whatever your notified date — we prepare your ERP ahead of the ZATCA letter, not after it.",
      },
      {
        icon: "FileCode",
        title: "UBL 2.1 Generation",
        body: "We generate the UUID, invoice hash, counter value and QR, and convert each invoice into ZATCA's UBL 2.1 XML representation.",
      },
      {
        icon: "ShieldCheck",
        title: "Validation Before Submission",
        body: "Comprehensive pre-submission checks against ZATCA's data dictionary so invoices clear the Fatoora portal without retries.",
      },
      {
        icon: "FilePdf",
        title: "PDF/A-3 with Embedded XML",
        body: "The Phase 2 QR and cleared XML are embedded back into a PDF/A-3, ready for the buyer and for audit.",
      },
      {
        icon: "PaperPlaneTilt",
        title: "Buyer Delivery Automation",
        body: "Cleared invoices are delivered to buyers automatically, with delivery status visible to your finance team.",
      },
      {
        icon: "QrCode",
        title: "Cryptographic Stamp & QR",
        body: "Standard invoices are cleared in real time; simplified invoices are reported within 24 hours with the required stamp and QR.",
      },
    ],
    faqs: [
      {
        question: "What changes between ZATCA Phase 1 and Phase 2?",
        answer:
          "Phase 1 (Generation, from December 2021) required structured e-invoices with a QR code. Phase 2 (Integration, from January 2023, in waves) adds direct integration with the Fatoora platform: UBL 2.1 XML, cryptographic stamping, UUID and hash, with real-time clearance for standard invoices and 24-hour reporting for simplified invoices.",
      },
      {
        question: "Which wave applies to my business?",
        answer:
          "ZATCA notifies businesses of their Phase 2 integration date in waves, originally banded by VAT-subject revenue and moving to smaller taxpayers over time. ZATCA gives at least six months' notice, so the practical answer is to be integration-ready ahead of your notified date.",
      },
      {
        question: "Can you integrate my ERP without rebuilding it?",
        answer:
          "Yes. Whether you run SAP, Oracle Fusion or EBS, Microsoft Dynamics, NetSuite, Odoo, Zoho, Tally or Sage, we connect it to ZATCA through an integration layer that reads invoice data, generates the compliant UBL 2.1 XML, applies the stamp and QR, and handles clearance and reporting — without re-implementing your ERP.",
      },
      {
        question: "Are you tied to a specific ASP?",
        answer:
          "No. Cloudare is ASP-neutral. We integrate with your chosen accredited solution provider, or operate the pipeline directly, depending on your architecture and procurement.",
      },
      {
        question: "What is the difference between a standard and a simplified invoice?",
        answer:
          "Standard (tax) invoices are B2B/B2G and require clearance by ZATCA before sharing with the buyer. Simplified invoices are B2C and are reported to ZATCA within 24 hours of issue.",
      },
    ],
  },

  uae: {
    slug: "uae",
    countryCode: "AE",
    hero: {
      eyebrow: "FTA | United Arab Emirates | PINT AE",
      h1: "UAE PINT AE e-invoicing for your ERP",
      subhead:
        "FTA-mandated e-invoicing on the PEPPOL-based five-corner model. ASP-neutral implementation across SAP, Oracle, Microsoft Dynamics, NetSuite, Odoo, Zoho, Tally and Sage.",
    },
    snapshot: [
      { label: "Authority", value: "UAE FTA" },
      { label: "Format", value: "PINT AE (PEPPOL BIS)" },
      { label: "Model", value: "5-corner (DCTCE)" },
      { label: "Cloudare role", value: "Implementation, ASP-neutral" },
    ],
    whyTitle: "Why Cloudare for UAE e-invoicing",
    whyLede:
      "The UAE follows a PEPPOL-based exchange model, not a clearance portal. We connect whichever ERP you run to an accredited access point correctly the first time.",
    features: [
      {
        icon: "ShieldCheck",
        title: "PINT AE Conformance",
        body: "Documents generated to the PINT AE specification (the UAE PEPPOL BIS), validated before they leave your environment.",
      },
      {
        icon: "GitMerge",
        title: "DCTCE 5-Corner Model",
        body: "We implement the decentralised continuous transaction control and exchange flow between accredited access points and the FTA.",
      },
      {
        icon: "PlugsConnected",
        title: "ASP-Neutral Architecture",
        body: "Connect through your chosen accredited service provider; the integration layer is portable, not locked to one vendor.",
      },
      {
        icon: "Buildings",
        title: "Multi-Entity Support",
        body: "Group structures with multiple TRNs and entities are modelled cleanly, with per-entity routing and reporting.",
      },
      {
        icon: "Database",
        title: "Any ERP, One Approach",
        body: "SAP (DRC), Oracle Fusion and EBS, Microsoft Dynamics, NetSuite, Odoo, Zoho, Tally and Sage — connected via native tools, middleware or API as each system fits.",
      },
      {
        icon: "CalendarCheck",
        title: "FTA Onboarding Readiness",
        body: "We prepare your master data, TRN validation and access-point onboarding ahead of the mandate go-live.",
      },
    ],
    faqs: [
      {
        question: "Which ERPs can you make UAE-compliant?",
        answer:
          "Any of them. We work across SAP (Document and Reporting Compliance), Oracle Fusion and EBS, Microsoft Dynamics 365, NetSuite, Odoo, Zoho, Tally and Sage — plus other systems through a documented API. The UAE model requires exchange through an accredited access point regardless of ERP, and Cloudare builds that bridge for whichever system you run.",
      },
      {
        question: "Is my ERP natively compliant with the UAE mandate?",
        answer:
          "Not on its own. Your ERP produces the source invoice data, but the UAE model requires exchange through an accredited access point on the PEPPOL-based network. Cloudare bridges your ERP to an ASP using the most appropriate method for your platform — a native compliance module, middleware, or API integration.",
      },
      {
        question: "How is the UAE model different from KSA ZATCA?",
        answer:
          "ZATCA uses a clearance/reporting model with a national portal and cryptographic stamping. The UAE follows a five-corner PEPPOL model (PINT AE) where accredited access points exchange documents and report to the FTA. The data models and onboarding differ, so an integration built for one is not portable to the other without rework.",
      },
      {
        question: "When does the UAE mandate take effect?",
        answer:
          "The UAE has been phasing in its e-invoicing programme with a staged rollout for large taxpayers ahead of broader application. We confirm your applicable date against the latest FTA guidance and prepare your integration ahead of it.",
      },
    ],
  },

  india: {
    slug: "india",
    countryCode: "IN",
    hero: {
      eyebrow: "GST | India | IRP & E-Way Bill",
      h1: "India GST e-invoicing for your ERP",
      subhead:
        "IRP registration, IRN and signed QR, e-way bill, and 2A/2B reconciliation integrated with SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally or Sage.",
    },
    snapshot: [
      { label: "Authority", value: "GSTN / IRP" },
      { label: "Format", value: "GST e-invoice schema (INV-01)" },
      { label: "Scope", value: "IRN, e-way bill, 2A/2B" },
      { label: "Cloudare role", value: "ERP integration partner" },
    ],
    whyTitle: "Why Cloudare for India GST",
    whyLede:
      "GST e-invoicing is more than IRN generation — it is reconciliation, e-way bills and input-tax-credit protection wired into your ERP.",
    features: [
      {
        icon: "FileText",
        title: "E-Invoice IRP Integration",
        body: "Generate IRN and the signed QR through the IRP directly from your ERP, with failures surfaced and retried automatically.",
      },
      {
        icon: "TruckSimple",
        title: "E-Way Bill",
        body: "Generate and manage e-way bills for goods movement above threshold, linked to the source invoice in your ERP.",
      },
      {
        icon: "ArrowsLeftRight",
        title: "2A / 2B Reconciliation",
        body: "Match purchase data against GSTR-2A/2B to protect input tax credit and flag mismatches before payment runs.",
      },
      {
        icon: "HandPalm",
        title: "Vendor Payment Blocking",
        body: "Hold vendors whose invoices are not reflected in the portal, protecting ITC and reducing year-end reconciliation effort.",
      },
      {
        icon: "Percent",
        title: "TDS / Withholding",
        body: "Withholding and TDS handled within the payables flow, aligned with statutory rates and reporting.",
      },
      {
        icon: "MapPin",
        title: "Multi-State GSTIN",
        body: "Multiple GSTINs across states modelled with correct place-of-supply and per-registration reporting.",
      },
    ],
    faqs: [
      {
        question: "What is the difference between the e-invoice IRP and the e-way bill?",
        answer:
          "The Invoice Registration Portal (IRP) validates a B2B invoice and returns an IRN and signed QR — it concerns the invoice itself. The e-way bill authorises the movement of goods above a value threshold — it concerns transport. They are related but separate, and Cloudare automates both from your ERP.",
      },
      {
        question: "How do you handle 2A/2B reconciliation?",
        answer:
          "We integrate GST return data (GSTR-2A/2B) with your ERP's Payables (Oracle, SAP, Dynamics, NetSuite, Zoho, Tally and others) so input-tax-credit mismatches are flagged before payment runs. Vendors whose invoices are not reflected in the portal can be held for review.",
      },
      {
        question: "Which ERP versions do you support for GST e-invoicing?",
        answer:
          "SAP, Oracle Fusion Cloud and EBS (R12/R12.2), Microsoft Dynamics, NetSuite, Odoo, Zoho, Tally and Sage. For other systems we expose a documented API the source system can call.",
      },
    ],
  },

  malaysia: {
    slug: "malaysia",
    countryCode: "MY",
    hero: {
      eyebrow: "LHDN | Malaysia | MyInvois",
      h1: "Malaysia LHDN e-invoicing for your ERP",
      subhead:
        "MyInvois e-invoicing aligned to LHDN's phased rollout — ERP integration, validation, consolidation and archival.",
    },
    snapshot: [
      { label: "Authority", value: "LHDN (IRBM)" },
      { label: "Platform", value: "MyInvois" },
      { label: "Rollout", value: "Phased by turnover" },
      { label: "Cloudare role", value: "Implementation, ERP-native" },
    ],
    whyTitle: "Why Cloudare for Malaysia e-invoicing",
    whyLede:
      "LHDN's MyInvois is a phased, turnover-banded rollout. We align your ERP and POS to it without slowing your operations.",
    features: [
      {
        icon: "ShieldCheck",
        title: "LHDN-Compliant Validation",
        body: "Pre-submission validation against LHDN's schema and business rules so invoices clear MyInvois on the first attempt.",
      },
      {
        icon: "PlugsConnected",
        title: "MyInvois API Integration",
        body: "Direct integration with the MyInvois API from SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally, Sage or your POS, with acknowledgements handled automatically.",
      },
      {
        icon: "ChartLine",
        title: "Real-Time Status Tracking",
        body: "Live status of submissions, rejections and pending acknowledgements so finance always knows where each invoice stands.",
      },
      {
        icon: "Database",
        title: "Master Data Sync",
        body: "Keep tax identifiers, classifications and buyer data in sync between your ERP and MyInvois.",
      },
      {
        icon: "CalendarCheck",
        title: "Phased Rollout Support",
        body: "We confirm your applicable turnover band and date, and prepare your integration ahead of it.",
      },
      {
        icon: "ListChecks",
        title: "Audit Trail & Reporting",
        body: "Tamper-evident archival of submitted invoices and IRBM responses, retained per Malaysian record-keeping rules.",
      },
    ],
    faqs: [
      {
        question: "What is the LHDN e-invoicing threshold and timeline?",
        answer:
          "LHDN is rolling out MyInvois in phases banded by annual turnover, expanding toward all businesses over time. Because thresholds and dates have been adjusted during rollout, we confirm your applicable date against the latest LHDN guidance.",
      },
      {
        question: "How does a POS-to-LHDN integration work for retail and hospitality?",
        answer:
          "High-volume B2C environments use consolidated e-invoices rather than one cleared document per transaction. We connect the POS to LHDN via a validation and submission layer, batching consumer transactions and handling acknowledgements without slowing checkout.",
      },
      {
        question: "Do you support self-billed e-invoices?",
        answer:
          "Yes — we cover the LHDN scenarios that require self-billed e-invoices, including foreign suppliers, agents, e-commerce settlements and certain payouts.",
      },
    ],
  },

  france: {
    slug: "france",
    countryCode: "FR",
    locales: ["en", "fr"],
    hero: {
      eyebrow: "DGFiP | France | Facture électronique",
      h1: "France e-invoicing and e-reporting for your ERP",
      subhead:
        "France's reforme de la facturation electronique: e-invoicing via Partner Dematerialization Platforms (PDP) and e-reporting to the DGFiP, integrated with your ERP.",
    },
    snapshot: [
      { label: "Authority", value: "DGFiP" },
      { label: "Format", value: "Factur-X / UBL / CII" },
      { label: "Model", value: "PDP + e-reporting" },
      { label: "Cloudare role", value: "Implementation, PDP-neutral" },
    ],
    whyTitle: "Why Cloudare for French e-invoicing",
    whyLede:
      "France combines mandatory B2B e-invoicing through certified platforms (PDP) with e-reporting of B2C and cross-border data. We wire both into your ERP.",
    features: [
      {
        icon: "ShieldCheck",
        title: "PDP-Ready Documents",
        body: "Invoices generated in the accepted French formats (Factur-X, UBL, CII) and validated before they reach a Partner Dematerialization Platform.",
      },
      {
        icon: "GitMerge",
        title: "PDP Routing",
        body: "We connect your ERP to your chosen PDP for issuance and receipt of structured invoices, PDP-neutral by design.",
      },
      {
        icon: "ChartLine",
        title: "E-Reporting",
        body: "Transmission of B2C and cross-border transaction and payment data to the DGFiP, alongside the B2B e-invoicing flow.",
      },
      {
        icon: "FileCode",
        title: "Factur-X Hybrid",
        body: "Generate Factur-X: a human-readable PDF/A-3 with embedded structured XML, the format favoured across French B2B.",
      },
      {
        icon: "Buildings",
        title: "Multi-Entity SIREN/SIRET",
        body: "Group structures with multiple SIREN/SIRET identifiers modelled with correct routing and reporting per entity.",
      },
      {
        icon: "CalendarCheck",
        title: "Phased Readiness",
        body: "We align to the French reform's phased calendar — receiving capability first, then issuance by company size — and prepare ahead of each date.",
      },
    ],
    faqs: [
      {
        question: "What is a PDP and do I need one?",
        answer:
          "A Partner Dematerialization Platform (Plateforme de Dematerialisation Partenaire) is a state-registered platform through which French B2B e-invoices are issued and received. Most businesses will route through a PDP. Cloudare connects your ERP to your chosen PDP and is platform-neutral.",
      },
      {
        question: "What is the difference between e-invoicing and e-reporting in France?",
        answer:
          "E-invoicing covers domestic B2B transactions, exchanged as structured invoices through PDPs. E-reporting covers transaction and payment data for B2C and cross-border flows, transmitted to the DGFiP. Most French businesses need both.",
      },
      {
        question: "What is Factur-X?",
        answer:
          "Factur-X is a hybrid format: a PDF/A-3 that a human can read with structured XML embedded inside it that a machine can process. It is widely used in French B2B and is one of the accepted formats under the reform.",
      },
      {
        question: "Which formats does France accept?",
        answer:
          "The reform accepts structured and hybrid formats including Factur-X, UBL and CII. Cloudare generates the format your counterparties and PDP require from your ERP data — SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally or Sage.",
      },
    ],
    // French translation of the key content
    fr: {
      hero: {
        eyebrow: "DGFiP | France | Facture électronique",
        h1: "Facturation électronique en France pour votre ERP",
        subhead:
          "La réforme de la facturation électronique : facturation via les Plateformes de Dématérialisation Partenaires (PDP) et e-reporting à la DGFiP, intégrés à votre ERP.",
      },
      whyTitle: "Pourquoi Cloudare pour la facturation électronique française",
      whyLede:
        "La France combine la facturation électronique B2B obligatoire via des plateformes certifiées (PDP) et l'e-reporting des données B2C et transfrontalières. Nous intégrons les deux à votre ERP.",
      snapshot: [
        { label: "Administration", value: "DGFiP" },
        { label: "Format", value: "Factur-X / UBL / CII" },
        { label: "Modèle", value: "PDP + e-reporting" },
        { label: "Rôle de Cloudare", value: "Mise en œuvre, neutre vis-à-vis des PDP" },
      ],
      features: [
        {
          icon: "ShieldCheck",
          title: "Documents prêts pour les PDP",
          body: "Factures générées dans les formats français acceptés (Factur-X, UBL, CII) et validées avant leur transmission à une Plateforme de Dématérialisation Partenaire.",
        },
        {
          icon: "GitMerge",
          title: "Routage PDP",
          body: "Nous connectons votre ERP à la PDP de votre choix pour l'émission et la réception des factures structurées, de manière neutre.",
        },
        {
          icon: "ChartLine",
          title: "E-reporting",
          body: "Transmission des données de transaction et de paiement B2C et transfrontalières à la DGFiP, en parallèle du flux de facturation B2B.",
        },
        {
          icon: "FileCode",
          title: "Factur-X hybride",
          body: "Génération de Factur-X : un PDF/A-3 lisible avec un XML structuré intégré, le format privilégié dans le B2B français.",
        },
        {
          icon: "Buildings",
          title: "Multi-entités SIREN/SIRET",
          body: "Les structures de groupe avec plusieurs identifiants SIREN/SIRET sont modélisées avec un routage et un reporting corrects par entité.",
        },
        {
          icon: "CalendarCheck",
          title: "Préparation par phases",
          body: "Nous nous alignons sur le calendrier progressif de la réforme — réception d'abord, puis émission selon la taille de l'entreprise — et préparons en amont de chaque échéance.",
        },
      ],
      faqs: [
        {
          question: "Qu'est-ce qu'une PDP et en ai-je besoin ?",
          answer:
            "Une Plateforme de Dématérialisation Partenaire est une plateforme immatriculée par l'État par laquelle les factures électroniques B2B françaises sont émises et reçues. La plupart des entreprises passeront par une PDP. Cloudare connecte votre ERP à la PDP de votre choix, de manière neutre.",
        },
        {
          question: "Quelle est la différence entre facturation électronique et e-reporting ?",
          answer:
            "La facturation électronique couvre les transactions B2B nationales, échangées sous forme de factures structurées via les PDP. L'e-reporting couvre les données de transaction et de paiement pour les flux B2C et transfrontaliers, transmises à la DGFiP. La plupart des entreprises françaises ont besoin des deux.",
        },
        {
          question: "Qu'est-ce que Factur-X ?",
          answer:
            "Factur-X est un format hybride : un PDF/A-3 lisible par un humain avec un XML structuré intégré qu'une machine peut traiter. Il est largement utilisé dans le B2B français et fait partie des formats acceptés par la réforme.",
        },
        {
          question: "Quels formats la France accepte-t-elle ?",
          answer:
            "La réforme accepte des formats structurés et hybrides, notamment Factur-X, UBL et CII. Cloudare génère le format requis par vos partenaires et votre PDP à partir des données de votre ERP — SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally ou Sage.",
        },
      ],
    },
  },

  eu: {
    slug: "eu",
    countryCode: "EU",
    hero: {
      eyebrow: "PEPPOL | European Union | ViDA",
      h1: "PEPPOL and EU e-invoicing for your ERP",
      subhead:
        "Cross-border e-invoicing over the PEPPOL network, aligned to EN 16931 and the EU's VAT in the Digital Age (ViDA) direction — implemented on SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally or Sage.",
    },
    snapshot: [
      { label: "Network", value: "PEPPOL (4/5-corner)" },
      { label: "Standard", value: "EN 16931 (UBL / CII)" },
      { label: "Direction", value: "ViDA" },
      { label: "Cloudare role", value: "Access-point-neutral" },
    ],
    whyTitle: "Why Cloudare for PEPPOL and EU e-invoicing",
    whyLede:
      "PEPPOL is the common rail under most EU mandates. Implement it once, correctly, and individual country mandates become configuration rather than rebuilds.",
    features: [
      {
        icon: "GitMerge",
        title: "PEPPOL 4/5-Corner",
        body: "Send and receive structured documents through certified PEPPOL access points across the European network.",
      },
      {
        icon: "FileCode",
        title: "EN 16931 Compliance",
        body: "Documents generated to the European standard (UBL or CII), the basis for most member-state mandates.",
      },
      {
        icon: "PlugsConnected",
        title: "Access-Point-Neutral",
        body: "Connect through your chosen certified access point; the integration is portable, not locked to one provider.",
      },
      {
        icon: "Buildings",
        title: "Multi-Country Rollout",
        body: "One PEPPOL integration supports multiple member-state mandates, with per-country profiles layered on top.",
      },
      {
        icon: "ChartLine",
        title: "ViDA Readiness",
        body: "Architected for the EU's VAT in the Digital Age direction toward near-real-time digital reporting.",
      },
      {
        icon: "ListChecks",
        title: "Validation & Archival",
        body: "Pre-send validation against country profiles, with compliant archival of sent and received documents.",
      },
    ],
    faqs: [
      {
        question: "What is PEPPOL?",
        answer:
          "PEPPOL is a network and set of specifications for exchanging structured business documents (including e-invoices) across borders. Certified access points connect senders and receivers, and it underpins many European e-invoicing mandates.",
      },
      {
        question: "How does PEPPOL relate to individual country mandates?",
        answer:
          "Many EU member states base their mandates on PEPPOL and the EN 16931 standard. Implementing PEPPOL once gives you a common rail, after which each country mandate is largely a matter of applying the correct profile and rules rather than a fresh build.",
      },
      {
        question: "What is ViDA?",
        answer:
          "VAT in the Digital Age (ViDA) is the EU's direction toward harmonised digital reporting and e-invoicing across member states. Building on PEPPOL and EN 16931 positions you for that direction.",
      },
      {
        question: "Which ERPs do you connect to PEPPOL?",
        answer:
          "SAP (S/4HANA and ECC via DRC), Oracle Fusion and EBS, Microsoft Dynamics 365, NetSuite, Odoo, Zoho, Tally and Sage — plus other systems via a documented API. We are access-point-neutral and integrate with your chosen certified provider.",
      },
    ],
  },
};

// Order for nav / sitemap
export const TAX_ORDER = ["ksa", "uae", "india", "malaysia", "france", "eu"];
